import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetUsersQuery } from '../../../store/api/usersApi';
import { formatAmountExact } from '../../../utils/amountFormatter';
import { formatDate, formatRelativeTime } from '../../../utils/dateFormatter';

export default function UsersListPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [isBanned, setIsBanned] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [sortBy, setSortBy] = useState<'createdAt' | 'balance' | 'lastActiveAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, error } = useGetUsersQuery({
    page,
    limit,
    search: search || undefined,
    isBanned: isBanned ?? undefined,
    isAdmin: isAdmin ?? undefined,
    sortBy,
    sortOrder,
  });

  const users = data?.data?.users || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
  };

  const clearFilters = () => {
    setSearch('');
    setIsBanned(null);
    setIsAdmin(null);
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.users.error.title') || 'Error loading users'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.users.title') || 'Users Management'}
          </h1>
          {pagination && (
            <div className="bg-brand-accent/20 border border-brand-accent/50 rounded-lg px-3 sm:px-4 py-2 w-fit">
              <span className="text-xs sm:text-sm font-medium text-neutral-medium">
                {t('admin.users.total') || 'Total Users'}:{' '}
              </span>
              <span className="text-base sm:text-lg font-heading font-bold text-brand-accent">
                {pagination.total}
              </span>
            </div>
          )}
        </div>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.users.subtitle') || 'Manage all users in the system'}
        </p>
      </div>

      {/* Filters with animation */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50 mb-4 sm:mb-6 transition-all duration-300 hover:border-brand-accent/30"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Search */}
            <div
              className="sm:col-span-2 lg:col-span-1"
              style={{
                animation: 'fadeIn 0.5s ease-out 0.2s both',
              }}
            >
              <label htmlFor="search" className="block text-sm font-medium text-neutral-light mb-2">
                {t('common.search') || 'Search'}
              </label>
              <input
                id="search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin.users.filters.searchPlaceholder') || 'Search by name, username, or ID...'}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              />
            </div>

            {/* Banned Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.3s both',
              }}
            >
              <label htmlFor="isBanned" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.users.filters.banStatus') || 'Ban Status'}
              </label>
              <select
                id="isBanned"
                value={isBanned === null ? 'all' : isBanned.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  setIsBanned(value === 'all' ? null : value === 'true');
                }}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              >
                <option value="all">{t('admin.users.filters.all') || 'All'}</option>
                <option value="false">{t('admin.users.filters.notBanned') || 'Not Banned'}</option>
                <option value="true">{t('admin.users.filters.banned') || 'Banned'}</option>
              </select>
            </div>

            {/* Admin Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.4s both',
              }}
            >
              <label htmlFor="isAdmin" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.users.filters.adminStatus') || 'Admin Status'}
              </label>
              <select
                id="isAdmin"
                value={isAdmin === null ? 'all' : isAdmin.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  setIsAdmin(value === 'all' ? null : value === 'true');
                }}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              >
                <option value="all">{t('admin.users.filters.all') || 'All'}</option>
                <option value="false">{t('admin.users.filters.notAdmin') || 'Not Admin'}</option>
                <option value="true">{t('admin.users.filters.admin') || 'Admin'}</option>
              </select>
            </div>

            {/* Sort By */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.5s both',
              }}
            >
              <label htmlFor="sortBy" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.users.filters.sortBy') || 'Sort By'}
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'createdAt' | 'balance' | 'lastActiveAt')}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              >
                <option value="createdAt">{t('admin.users.filters.createdAt') || 'Created Date'}</option>
                <option value="balance">{t('admin.users.filters.balance') || 'Balance'}</option>
                <option value="lastActiveAt">{t('admin.users.filters.lastActiveAt') || 'Last Active'}</option>
              </select>
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row gap-3"
            style={{
              animation: 'fadeIn 0.5s ease-out 0.6s both',
            }}
          >
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
            >
              {t('common.search') || 'Search'}
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="w-full sm:w-auto px-6 py-2 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
            >
              {t('admin.users.filters.clear') || 'Clear Filters'}
            </button>
          </div>
        </form>
      </div>

      {/* Users Table - Desktop */}
      {users.length === 0 ? (
        <div
          className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-8 sm:p-12 border border-neutral-dark/50 text-center"
          style={{
            animation: 'fadeIn 0.5s ease-out 0.3s both',
          }}
        >
          <p className="text-base sm:text-lg text-neutral-medium">
            {t('admin.users.empty') || 'No users found'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div
            className="hidden lg:block bg-neutral-dark/50 backdrop-blur-lg rounded-lg border border-neutral-dark/50 overflow-hidden transition-all duration-300 hover:border-brand-accent/30"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/80 border-b border-neutral-medium">
                  <tr>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.telegramId') || 'Telegram ID'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.name') || 'Name'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.username') || 'Username'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.balance') || 'Balance'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.status') || 'Status'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.lastActive') || 'Last Active'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.created') || 'Created'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.users.table.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-medium">
                  {users.map((user, index) => (
                    <tr
                      key={user.telegramId || user._id}
                      className="hover:bg-neutral-dark/50 transition-all duration-200 hover:shadow-lg hover:shadow-brand-accent/5"
                      style={{
                        animation: `fadeIn 0.4s ease-out ${index * 30}ms both`,
                      }}
                    >
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-light">
                        <span className="font-mono text-xs xl:text-sm">{user.telegramId || '-'}</span>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-light">
                        <span className="font-medium text-sm xl:text-base">
                          {user.firstName} {user.lastName || ''}
                        </span>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-medium">
                        {user.username ? (
                          <span className="text-brand-accent text-sm xl:text-base">@{user.username}</span>
                        ) : (
                          <span className="text-neutral-medium">-</span>
                        )}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-brand-accent font-semibold text-sm xl:text-base">
                        ${formatAmountExact(user.balance)}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1 xl:gap-2">
                          {user.isAdmin && (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-500/20 text-blue-400 border border-blue-500/50 transition-transform duration-200 hover:scale-105">
                              {t('admin.users.status.admin') || 'Admin'}
                            </span>
                          )}
                          {user.isBanned ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-red-500/20 text-red-400 border border-red-500/50 transition-transform duration-200 hover:scale-105">
                              {t('admin.users.status.banned') || 'Banned'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50 transition-transform duration-200 hover:scale-105">
                              {t('admin.users.status.active') || 'Active'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-medium text-xs xl:text-sm">
                        {user.lastActiveAt && typeof user.lastActiveAt === 'string' ? (
                          <div className="flex flex-col">
                            <span className="text-neutral-light">{formatDate(user.lastActiveAt as string, 'PP', currentLanguage)}</span>
                            <span className="text-xs text-neutral-medium mt-1">
                              {formatRelativeTime(user.lastActiveAt as string, currentLanguage)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-neutral-medium">-</span>
                        )}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-medium text-xs xl:text-sm">
                        {formatDate(user.createdAt, 'PP', currentLanguage)}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                        <Link
                          to={`/admin/users/${user.telegramId || user._id}`}
                          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1 text-sm xl:text-base"
                        >
                          {t('admin.users.table.view') || 'View'}
                          <svg
                            className="w-3 h-3 xl:w-4 xl:h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div
            className="lg:hidden space-y-3 sm:space-y-4"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            {users.map((user, index) => (
              <div
                key={user.telegramId || user._id}
                className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-5 border border-neutral-dark/50 hover:border-brand-accent/50 transition-all duration-200"
                style={{
                  animation: `fadeIn 0.4s ease-out ${index * 50}ms both`,
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base sm:text-lg font-heading font-semibold text-neutral-light">
                        {user.firstName} {user.lastName || ''}
                      </h3>
                      {user.isAdmin && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-500/20 text-blue-400 border border-blue-500/50">
                          {t('admin.users.status.admin') || 'Admin'}
                        </span>
                      )}
                      {user.isBanned ? (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-red-500/20 text-red-400 border border-red-500/50">
                          {t('admin.users.status.banned') || 'Banned'}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50">
                          {t('admin.users.status.active') || 'Active'}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.users.table.telegramId') || 'Telegram ID'}:
                        </span>
                        <span className="text-neutral-light font-mono">{user.telegramId || '-'}</span>
                      </div>
                      {user.username && (
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-medium font-medium">
                            {t('admin.users.table.username') || 'Username'}:
                          </span>
                          <span className="text-brand-accent">@{user.username}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.users.table.balance') || 'Balance'}:
                        </span>
                        <span className="text-brand-accent font-semibold">${formatAmountExact(user.balance)}</span>
                      </div>
                      {user.lastActiveAt && typeof user.lastActiveAt === 'string' && (
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-medium font-medium">
                            {t('admin.users.table.lastActive') || 'Last Active'}:
                          </span>
                          <span className="text-neutral-light text-xs">
                            {formatRelativeTime(user.lastActiveAt as string, currentLanguage)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.users.table.created') || 'Created'}:
                        </span>
                        <span className="text-neutral-light text-xs">
                          {formatDate(user.createdAt, 'PP', currentLanguage)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/admin/users/${user.telegramId || user._id}`}
                  className="block w-full mt-3 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 text-center text-sm"
                >
                  {t('admin.users.table.view') || 'View Details'}
                </Link>
              </div>
            ))}
          </div>

          {/* Pagination with animation */}
          {pagination && pagination.pages > 1 && (
            <div
              className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0"
              style={{
                animation: 'fadeIn 0.5s ease-out 0.4s both',
              }}
            >
              <div className="text-neutral-medium text-xs sm:text-sm text-center sm:text-left">
                {t('admin.users.pagination.showing') || 'Showing'}{' '}
                <span className="text-brand-accent font-semibold">
                  {(pagination.page - 1) * pagination.limit + 1}
                </span>{' '}
                -{' '}
                <span className="text-brand-accent font-semibold">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                {t('admin.users.pagination.of') || 'of'}{' '}
                <span className="text-brand-accent font-semibold">{pagination.total}</span>{' '}
                {t('admin.users.pagination.users') || 'users'}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page === 1}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100 text-sm sm:text-base"
                >
                  {t('admin.users.pagination.previous') || 'Previous'}
                </button>
                <span className="hidden sm:inline-flex items-center px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light text-sm sm:text-base">
                  {t('admin.users.pagination.page') || 'Page'}{' '}
                  <span className="text-brand-accent font-semibold mx-1">{pagination.page}</span>{' '}
                  {t('admin.users.pagination.of') || 'of'}{' '}
                  <span className="text-brand-accent font-semibold mx-1">{pagination.pages}</span>
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={pagination.page === pagination.pages}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100 text-sm sm:text-base"
                >
                  {t('admin.users.pagination.next') || 'Next'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
